/**
 * Apollo `InMemoryCache` field-policy merge function for offset-based
 * paginated list queries (myProjects, myLibraries, communityLibraries, ...).
 *
 * Behavior:
 * - `offset === -1`: sentinel for "cache was manipulated", overwrite with incoming.
 * - `offset === 0`: a fresh query (new filters/sort) — replace entirely so stale
 *   results from previous filters don't persist. NOTE: callers that merely want to
 *   *refresh* an already-paginated list must request the full loaded range as
 *   `limit` (see computeLibraryRefreshVariables), otherwise the list is truncated
 *   back to the first page.
 * - `offset > 0`: pagination — overlay incoming onto existing at the given offset.
 */
export interface MergeListsOptions {
  args?: { offset?: number | null } | null;
}

export function mergeLists<T>(
  existing: T[] | undefined,
  incoming: T[],
  { args }: MergeListsOptions,
): T[] {
  const offset = args?.offset || 0;
  if (offset === -1) {
    // Cache has been manipulated, manually overwrite
    return incoming;
  }

  // When offset is 0, this is a fresh query (new filters/sort), not pagination.
  // Replace entirely so stale results from previous filters don't persist.
  if (offset === 0) {
    return incoming;
  }

  // Slicing is necessary because the existing data is
  // immutable, and frozen in development.
  const merged = existing ? existing.slice(0) : [];
  for (let i = 0; i < incoming.length; i += 1) {
    merged[offset + i] = incoming[i];
  }
  return merged;
}
